/**
 * MAHI — the wait state for a route that isn't on screen yet: its chunk is
 * still downloading, or the auth check is still in flight. Replaces the old
 * spinning-orb (MahiRobot) wait screen, which read as a second, unrelated
 * loading moment bolted onto the app rather than the app arriving.
 *
 * Renders the exact masthead row every page's own header uses (MahiHeader),
 * with the MAHI letters flying into the wordmark slot instead of a static
 * mark — the same choreography as the session-entry animation (MahiIntro;
 * see mahiLetterMotion for the shared timing). Because every page now opens
 * with that identical row, the letters land precisely where the destination
 * page's own wordmark will sit. So when this unmounts and the real page's
 * header mounts in its place, there's nothing to cross-fade or fly: the
 * mark is simply already where it needs to be — the letters "merge into"
 * the next page's logo by already being it.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { MAHI_GLYPHS, MAHI_VIEWBOX } from './MahiLogo';
import { mahiLetterContainer, mahiLetterVariant } from './mahiLetterMotion';
import MahiHeader from './MahiHeader';
import useReducedMotion from '../../hooks/useReducedMotion';

export default function MahiRouteLoader() {
  const reduced = useReducedMotion();

  return (
    <div className="fixed inset-0 bg-white flex flex-col">
      <MahiHeader
        wordmark={(
          <svg
            viewBox={MAHI_VIEWBOX}
            fill="currentColor"
            className="h-[18px] w-auto text-slate-700"
            role="img"
            aria-label="MAHI"
          >
            {reduced ? (
              <g>
                {MAHI_GLYPHS.map((glyph) => (
                  <path key={glyph.id} transform={glyph.transform} d={glyph.d} />
                ))}
              </g>
            ) : (
              <motion.g variants={mahiLetterContainer} initial="hidden" animate="visible">
                {MAHI_GLYPHS.map((glyph) => (
                  <motion.g key={glyph.id} variants={mahiLetterVariant}>
                    <path transform={glyph.transform} d={glyph.d} />
                  </motion.g>
                ))}
              </motion.g>
            )}
          </svg>
        )}
      />
    </div>
  );
}
